﻿import { useCallback, useEffect, useState } from 'react';
import { Loader2, Star } from 'lucide-react';
import { useToast } from '@foundation/hooks/use-toast';
import { useUser } from '@foundation/contextapi/UserContext';
import { getActiveWorkspaceId } from '../../../../header/activeWorkspace';
import {
  fetchAdminFeedback,
  type AdminFeedbackItem,
  type FeedbackStats,
} from '../../services/policyFeedbackService';

const RATING_OPTIONS: { label: string; value: number | undefined }[] = [
  { label: 'All ratings', value: undefined },
  { label: '4★ & up', value: 4 },
  { label: '3★ & up', value: 3 },
  { label: '2★ & up', value: 2 },
  { label: '1★ & up', value: 1 },
];

const Stars = ({ value }: { value: number }) => (
  <span className="inline-flex" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`h-4 w-4 ${value >= n ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
    ))}
  </span>
);

/** A truncatable cell with "more/less" toggle, sharing the row's expanded state. */
const LongText = ({ text, open }: { text: string | null; open: boolean }) => {
  if (!text) return <span className="text-muted-foreground">—</span>;
  const isLong = text.length > 80;
  return (
    <>
      <span>{open || !isLong ? text : `${text.slice(0, 80)}…`}</span>
      {isLong && <span className="text-muted-foreground">{open ? ' (less)' : '…'}</span>}
    </>
  );
};

export const PolicyFeedbackPage = () => {
  const { toast } = useToast();
  const { user } = useUser();
  const [items, setItems] = useState<AdminFeedbackItem[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [minRating, setMinRating] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const orgId = getActiveWorkspaceId() ?? undefined;
      const data = await fetchAdminFeedback({ minRating, limit: 100 }, user.id, orgId);
      setItems(data.items);
      setStats(data.stats);
      setForbidden(false);
    } catch (e) {
      if (e instanceof Error && e.message.includes('403')) setForbidden(true);
      else toast({ title: 'Error', description: 'Failed to load feedback', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, minRating, toast]);

  useEffect(() => { void load(); }, [load]);

  if (forbidden) {
    return <div className="p-10 text-center text-muted-foreground">Admin access required.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Policy Feedback</h1>
        <p className="text-sm text-muted-foreground">Ratings, comments, and quality reviews from finalised policies across organisations.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">Average rating</div>
            <div className="text-3xl font-bold flex items-center gap-2">
              {stats.averageRating.toFixed(2)}
              <Star className="h-5 w-5 fill-yellow-400 text-yellow-400" />
            </div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground">Total feedback</div>
            <div className="text-3xl font-bold">{stats.totalCount}</div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <div className="text-xs text-muted-foreground mb-1">Distribution</div>
            {[5, 4, 3, 2, 1].map((n) => {
              const count = stats.ratingDistribution[n as 1 | 2 | 3 | 4 | 5];
              const pct = stats.totalCount > 0 ? (count / stats.totalCount) * 100 : 0;
              return (
                <div key={n} className="flex items-center gap-2 text-xs">
                  <span className="w-4">{n}★</span>
                  <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                    <div className="h-full bg-yellow-400" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-6 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-4">
        <select
          className="px-3 py-2 rounded-xl border border-border bg-background text-sm"
          value={minRating ?? ''}
          onChange={(e) => setMinRating(e.target.value === '' ? undefined : Number(e.target.value))}
        >
          {RATING_OPTIONS.map((o) => (
            <option key={o.label} value={o.value ?? ''}>{o.label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-muted-foreground">No feedback yet.</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border bg-muted/40">
                <th className="p-3">Rating</th>
                <th className="p-3">Comment</th>
                <th className="p-3">Quality review</th>
                <th className="p-3">Policy</th>
                <th className="p-3">Standard</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const open = expanded === it.id;
                const toggle = () => setExpanded(open ? null : it.id);
                const hasMore = (it.comment ?? '').length > 80 || (it.quality_review ?? '').length > 80;
                return (
                  <tr key={it.id} className="border-b border-border last:border-0 align-top">
                    <td className="p-3 whitespace-nowrap"><Stars value={it.rating} /></td>
                    <td className="p-3 max-w-sm">
                      {it.comment ? (
                        <button type="button" className="text-left" onClick={toggle}>
                          <LongText text={it.comment} open={open} />
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 max-w-sm">
                      {it.quality_review ? (
                        <button type="button" className="text-left" onClick={toggle}>
                          <LongText text={it.quality_review} open={open} />
                        </button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3">{it.policyName ?? '—'}</td>
                    <td className="p-3">{it.selectedDocument ?? '—'}</td>
                    <td className="p-3 whitespace-nowrap">{new Date(it.created_at).toLocaleDateString()}{hasMore && <span className="block text-xs text-[#33b864]">{open ? 'show less' : 'show more'}</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
